import { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { useAdminPermissions } from "@/hooks/useAdminPermissions";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import {
  Shield,
  Building2,
  MessageSquare,
  Globe,
  MapPin,
  Users,
  UserCog,
} from "lucide-react";
import { cn } from "@/lib/utils";
import bandflowAvatar from "@/assets/bandflow-avatar.png";

type NavItem = {
  label: string;
  href: string;
  icon: ReactNode;
  module: string;
};

const NAV_ITEMS: NavItem[] = [
  { label: "Admins y Roles", href: "/admin/admins", icon: <UserCog className="h-4 w-4" />, module: "admins_roles" },
  { label: "Salas", href: "/admin/venues", icon: <Building2 className="h-4 w-4" />, module: "salas" },
  { label: "Reviews", href: "/admin/reviews", icon: <MessageSquare className="h-4 w-4" />, module: "reviews" },
  { label: "SEO", href: "/admin/seo", icon: <Globe className="h-4 w-4" />, module: "seo" },
  { label: "Ciudades", href: "/admin/cities", icon: <MapPin className="h-4 w-4" />, module: "ciudades" },
  { label: "Usuarios", href: "/admin/users", icon: <Users className="h-4 w-4" />, module: "usuarios_comunidad" },
];

interface AdminLayoutProps {
  children: ReactNode;
  requiredModule?: string;
  requiredPermission?: string;
}

export function AdminLayout({ children, requiredModule, requiredPermission }: AdminLayoutProps) {
  useSeoMeta({ pageType: "admin" });
  const location = useLocation();
  const { canView, can, isLoading } = useAdminPermissions();

  const visibleItems = NAV_ITEMS.filter((item) => canView(item.module));

  const hasAccess = !requiredModule || !requiredPermission
    ? true
    : can(requiredModule, requiredPermission as any);

  return (
    <Layout>
      <div className="container py-6">
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Sidebar nav */}
          <aside className="shrink-0 lg:w-52">
            <div className="flex items-center gap-2 mb-4 px-2">
              <img src={bandflowAvatar} alt="Bandflow" className="h-5 w-5" />
              <span className="font-display text-sm font-bold">Administración</span>
            </div>
            <nav className="flex flex-row gap-1 overflow-x-auto lg:flex-col">
              {visibleItems.map((item) => {
                const active = location.pathname === item.href ||
                  (item.href === "/admin/users" && location.pathname.startsWith("/admin/users/")) ||
                  (item.href === "/admin/venues" && location.pathname.startsWith("/admin/venues/"));
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap",
                      active
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <p className="text-muted-foreground">Cargando permisos…</p>
              </div>
            ) : !hasAccess ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Shield className="h-10 w-10 text-muted-foreground mb-3" />
                <h2 className="font-display text-lg font-semibold">Sin permisos</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  No tienes permisos para acceder a esta sección.
                </p>
              </div>
            ) : (
              children
            )}
          </main>
        </div>
      </div>
    </Layout>
  );
}
